import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import Reveal, { Caption } from "@/components/Reveal";

export default function FAQSection() {
  const { t } = useLang();
  const items = t("faq.items");
  const [openIdx, setOpenIdx] = useState(null);

  if (!Array.isArray(items)) return null;

  return (
    <section id="faq" className="max-w-4xl mx-auto px-4 sm:px-8 py-20 sm:py-24" data-testid="faq-section">
      <Reveal className="text-center mb-10">
        <Caption>{t("faq.caption")}</Caption>
        <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight mt-4 text-ink">
          {t("faq.title")}
        </h2>
      </Reveal>
      <div className="space-y-3">
        {items.map((item, i) => {
          const open = openIdx === i;
          return (
            <Reveal key={i} delay={Math.min(i * 0.04, 0.3)}>
              <div
                className={`bg-white rounded-2xl border transition-colors ${open ? "border-terracotta/40" : "border-terracotta/10"}`}
                data-testid={`faq-item-${i}`}
              >
                <button
                  type="button"
                  data-testid={`faq-question-${i}`}
                  aria-expanded={open}
                  onClick={() => setOpenIdx(open ? null : i)}
                  className="w-full flex items-center justify-between gap-4 px-5 sm:px-7 py-5 text-left"
                >
                  <span className="font-medium text-ink text-[15px] sm:text-base">{item.q}</span>
                  <span
                    className={`shrink-0 w-8 h-8 rounded-full border flex items-center justify-center transition-all duration-300 ${
                      open ? "bg-terracotta border-terracotta text-sand rotate-45" : "border-ink/15 text-ink/50"
                    }`}
                  >
                    <Plus size={15} />
                  </span>
                </button>
                <AnimatePresence initial={false}>
                  {open && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.28, ease: "easeInOut" }}
                      className="overflow-hidden"
                    >
                      <p className="px-5 sm:px-7 pb-6 text-sm text-ink/65 leading-relaxed" data-testid={`faq-answer-${i}`}>
                        {item.a}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </Reveal>
          );
        })}
      </div>
      <Reveal className="text-center mt-8">
        <p className="text-xs text-ink/45">
          <Link to="/terms" data-testid="faq-terms-link" className="underline underline-offset-4 hover:text-terracotta transition-colors">
            {t("footer.terms")}
          </Link>
        </p>
      </Reveal>
    </section>
  );
}
